import React from 'react';
import { DocumentLineItemWorkOrder } from '../types/Types';
import { parseDecimalInput } from '../utils/decimalInput';

interface UseNestedDocumentSearchSortParams {
  localRows: DocumentLineItemWorkOrder[];
  columnOrder: string[];
  inputRefs: React.MutableRefObject<(HTMLInputElement | HTMLSelectElement | null)[][]>;
  focusLastRow: () => void;
}

/**
 * Search + column sort state for NestedDocumentTable's rows.
 * Extracted 1:1 from NestedDocumentTable.tsx (CLAUDE.md rule 9 split) — no behaviour change.
 */
export function useNestedDocumentSearchSort({
  localRows,
  columnOrder,
  inputRefs,
  focusLastRow,
}: UseNestedDocumentSearchSortParams) {
  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const [searchInput, setSearchInput] = React.useState<string>('');
  const [searchLoading, setSearchLoading] = React.useState<boolean>(false);
  const [isSearchOpen, setIsSearchOpen] = React.useState<boolean>(false);
  // --- Sorting State ---
  const [sortField, setSortField] = React.useState<string | null>(null);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');

  const parseNumber = (value?: string | number): number => parseDecimalInput(value) ?? 0;

  // Handle column sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and sort the local rows based on search term and sort field
  const filteredAndSortedRows = React.useMemo(() => {
    // First, filter the rows based on search term
    let filteredRows = localRows;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filteredRows = localRows.filter(row => {
        // Search in item code/ident
        const itemIdent = row.item?.ident?.toLowerCase() || '';
        const itemName = row.item?.name?.toLowerCase() || '';
        const price = row.purchasePrice?.toString().toLowerCase() || '';
        const valuationPrice = row.valuationPrice?.toString().toLowerCase() || '';
        const quantity = row.quantity?.toString().toLowerCase() || '';
        const serial = row.serial?.toLowerCase() || '';
        const deadline = row.deadline?.toLowerCase() || '';
        const priceValue = row.priceValue?.toString().toLowerCase() || '';

        return itemIdent.includes(searchLower) ||
               itemName.includes(searchLower) ||
               price.includes(searchLower) ||
               valuationPrice.includes(searchLower) ||
               quantity.includes(searchLower) ||
               serial.includes(searchLower) ||
               deadline.includes(searchLower) ||
               priceValue.includes(searchLower);
      });
    }

    // Then, sort the filtered rows
    if (!sortField) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'ident':
          aValue = a.item?.ident || '';
          bValue = b.item?.ident || '';
          break;
        case 'name':
          aValue = a.item?.name || '';
          bValue = b.item?.name || '';
          break;
        case 'purchasePrice':
          aValue = parseNumber(a.purchasePrice);
          bValue = parseNumber(b.purchasePrice);
          break;
        case 'valuationPrice':
          aValue = parseNumber(a.valuationPrice);
          bValue = parseNumber(b.valuationPrice);
          break;
        case 'quantity':
          aValue = parseNumber(a.quantity);
          bValue = parseNumber(b.quantity);
          break;
        case 'serial':
          aValue = a.serial || '';
          bValue = b.serial || '';
          break;
        case 'deadline':
          aValue = a.deadline || '';
          bValue = b.deadline || '';
          break;
        case 'priceValue':
          aValue = parseNumber(a.priceValue);
          bValue = parseNumber(b.priceValue);
          break;
        default:
          return 0;
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      // Handle numeric comparison
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [localRows, searchTerm, sortField, sortDirection]);

  // Reset sorting to createdAt when user starts typing in new row
  const resetSortingOnEdit = () => {
    if (sortField !== null) {
      setSortField(null);
      setSortDirection('asc');
    }
  };

  // Clear search function
  const clearSearch = () => {
    setSearchLoading(true);

    // Simulate clearing delay
    setTimeout(() => {
      setSearchInput('');
      setSearchTerm('');
      setSearchLoading(false);
      // Restore focus to last row after clearing search
      setTimeout(() => focusLastRow(), 100);
    }, 200); // 200ms delay for clear action
  };

  // Handle search with loading state
  const handleSearch = async () => {
    if (searchInput.trim() === searchTerm.trim()) return; // No change in search term

    setSearchLoading(true);

    // Simulate search delay like API call
    setTimeout(() => {
      setSearchTerm(searchInput);
      setSearchLoading(false);
      // Restore focus to first row after search
      setTimeout(() => {
        const priceColumnIndex = columnOrder.indexOf('purchasePrice') + 2;
        const firstRowInput = inputRefs.current[0]?.[priceColumnIndex];
        if (firstRowInput) {
          firstRowInput.focus();
          if (firstRowInput instanceof HTMLInputElement) {
            firstRowInput.select();
          }
        }
      }, 100);
    }, 300); // 300ms delay to show loading
  };

  return {
    searchInput,
    setSearchInput,
    searchLoading,
    isSearchOpen,
    setIsSearchOpen,
    sortField,
    sortDirection,
    handleSort,
    filteredAndSortedRows,
    resetSortingOnEdit,
    clearSearch,
    handleSearch,
  };
}

export default useNestedDocumentSearchSort;
